import { useCallback, useMemo, useRef } from 'react';
import { Box, CircularProgress, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useZoom } from 'context/ZoomContext';
import { calculateTableHeight, StyledTableCellEmptyMessage } from 'dashboard409a/components/ValuationsTable/common';
import { modelsColumns } from 'dashboard409a/pages/Workboard/common/modelsColConfig';
import { ModelsTableHeadColumn, ModelsTableProps } from './ModelsTable.types';
import ModelsTableHead from './ModelsTableHead/ModelsTableHead';
import ModelsTableRow from './ModelsTableRow/ModelsTableRow';

export const ModelsTable = ({
  isLoading,
  rows,
  onNoteSave,
  onStatusUpdate,
  orderBy,
  order,
  sortCriteria,
  onRequestSort,
  isNoteLoading = false,
  noteError = null,
  onRowClick,
  columnWidths,
  onColumnWidthChange,
}: ModelsTableProps) => {
  const parentRef = useRef<HTMLDivElement>(null);
  const { zoomLevel } = useZoom();

  const getItemKey = useCallback((index: number) => rows[index]?.id ?? index, [rows]);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 50,
    overscan: 20,
    getItemKey,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const firstVirtualItem = virtualItems[0];
  const lastVirtualItem = virtualItems.at(-1);
  const paddingTop = firstVirtualItem?.start ?? 0;
  const paddingBottom = lastVirtualItem ? Math.max(0, totalSize - lastVirtualItem.end) : 0;

  const tableHeight = useMemo(() => calculateTableHeight(zoomLevel, 200), [zoomLevel]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <TableContainer
      ref={parentRef}
      sx={{
        overflowX: 'auto',
        overflowY: 'auto',
        maxHeight: tableHeight,
      }}>
      <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
        <ModelsTableHead
          orderBy={orderBy}
          order={order}
          sortCriteria={sortCriteria}
          onRequestSort={onRequestSort}
          columns={modelsColumns as ModelsTableHeadColumn[]}
          columnWidths={columnWidths}
          onColumnWidthChange={onColumnWidthChange}
        />
        <TableBody>
          {virtualItems.length === 0 ? (
            <TableRow style={{ height: 33 }}>
              <StyledTableCellEmptyMessage colSpan={8}>No models found</StyledTableCellEmptyMessage>
            </TableRow>
          ) : (
            <>
              {paddingTop > 0 && (
                <TableRow>
                  <TableCell colSpan={8} style={{ padding: 0, height: paddingTop, border: 0, lineHeight: 0 }} />
                </TableRow>
              )}

              {virtualItems.map(virtualRow => {
                const row = rows[virtualRow.index];
                return (
                  <ModelsTableRow
                    key={virtualRow.key}
                    modelRow={row}
                    onRowClick={onRowClick}
                    onNoteSave={onNoteSave}
                    onStatusUpdate={onStatusUpdate}
                    isNoteLoading={isNoteLoading}
                    noteError={noteError}
                  />
                );
              })}
              {paddingBottom > 0 && (
                <TableRow>
                  <TableCell colSpan={8} style={{ padding: 0, height: paddingBottom, border: 0, lineHeight: 0 }} />
                </TableRow>
              )}
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
